import clsx from "clsx";
import { createElement, FC, Fragment, MouseEvent, useRef } from "react";

import {
  EmptyPanel,
  PulsingDots,
  TabPanel,
  TabSet,
} from "@tsmono/react/components";
import { useScrollDirection } from "@tsmono/react/hooks";

import { refreshLog } from "../../state/actions";
import {
  useEvalSpec,
  useSelectedLogDetails,
  useSelectedRunningMetrics,
} from "../../state/hooks";
import { useSelectedLogLoading } from "../../state/selectedLogDetails";
import { useStore } from "../../state/store";
import { useCurrentLogFile } from "../routing/currentSelection";
import { useLogNavigationAction } from "../routing/logNavigation";
import { toFullUrlMaybe } from "../routing/url";

import styles from "./LogView.module.css";
import { useErrorTabConfig } from "./tabs/ErrorTab";
import { useInfoTabConfig } from "./tabs/InfoTab";
import { useJsonTabConfig } from "./tabs/JsonTab";
import { useModelsTab } from "./tabs/ModelsTab";
import { useSamplesTabConfig } from "./tabs/SamplesTab";
import { useTaskTabConfig } from "./tabs/TaskTab";
import { useTimelineTab } from "./tabs/timeline/TimelineTab";
import { TitleView } from "./title-view/TitleView";
import { TabDescriptor } from "./types";

export const LogView: FC = () => {
  const divRef = useRef<HTMLDivElement>(null);

  const navigation = useLogNavigationAction();
  const logFile = useCurrentLogFile();

  const selectedLogDetails = useSelectedLogDetails();
  const logLoading = useSelectedLogLoading();
  const evalSpec = useEvalSpec();
  // Settled metrics only: the title view is decorative here — poll
  // loading/error surface through the samples tab's summaries instead.
  const runningMetrics = useSelectedRunningMetrics().data;

  // Use individual tab config hooks
  const samplesTabConfig = useSamplesTabConfig(selectedLogDetails?.status, () =>
    refreshLog(logFile)
  );

  const intoTabConfig = useInfoTabConfig(
    evalSpec,
    selectedLogDetails?.plan,
    selectedLogDetails?.error,
    selectedLogDetails?.results,
    selectedLogDetails?.status,
    selectedLogDetails?.metadata
  );

  const errorTabConfig = useErrorTabConfig(selectedLogDetails?.error);

  const taskTabConfig = useTaskTabConfig(
    evalSpec,
    selectedLogDetails?.stats,
    selectedLogDetails?.results?.early_stopping,
    selectedLogDetails?.tags,
    selectedLogDetails?.config_updates
  );

  const modelsTabConfig = useModelsTab(
    evalSpec,
    selectedLogDetails?.stats,
    selectedLogDetails?.status,
    selectedLogDetails?.config_updates
  );

  const timelineTabConfig = useTimelineTab(
    evalSpec,
    selectedLogDetails?.stats,
    selectedLogDetails?.status,
    selectedLogDetails?.config_updates,
    selectedLogDetails?.log_updates,
    selectedLogDetails?.results?.early_stopping
  );

  const jsonTabConfig = useJsonTabConfig(selectedLogDetails);

  // Heterogeneous tab configs each bind their own props type; `unknown`
  // can't unify them because TabDescriptor's `component` puts the prop type
  // in a contravariant position.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tabs: Record<string, TabDescriptor<any>> = {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    ...(samplesTabConfig ? { samples: samplesTabConfig } : {}),
    task: taskTabConfig,
    model: modelsTabConfig,
    timeline: timelineTabConfig,
    config: intoTabConfig,
    ...(selectedLogDetails?.error ? { error: errorTabConfig } : {}),
    json: jsonTabConfig,
  };

  const scrollRefs = Object.values(tabs).flatMap((tab) =>
    tab.scrollRef ? [tab.scrollRef] : []
  );

  const { hidden: titleCollapsed } = useScrollDirection(scrollRefs, {
    stayHiddenOnUpScroll: true,
  });

  const selectedTab = useStore((state) => state.app.tabs.workspace);
  // A sample's terminals view wants the height: the title shows collapsed
  // while one is mounted (derived, so leaving it shows the scroll state
  // again).
  const onSampleTerminals = useStore((state) => state.sample.terminalsViewOpen);
  const setSelectedTab = useStore((state) => state.appActions.setWorkspaceTab);

  const onSelected = (e: MouseEvent<HTMLElement>) => {
    const id = e.currentTarget.id;
    if (id) {
      setSelectedTab(id);
      navigation.selectTab(id);
    }
  };

  if (evalSpec === undefined) {
    return (
      <EmptyPanel>
        {logLoading ? <PulsingDots size="large" text="Loading log…" /> : null}
      </EmptyPanel>
    );
  } else {
    const tabTools = Object.values(tabs)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      .filter((tab) => tab !== undefined)
      .filter((tab) => {
        return tab.id === selectedTab;
      })
      .map((tab) => {
        if (tab.tools) {
          const tools = tab.tools();
          return tools;
        } else {
          return null;
        }
      });

    return (
      <Fragment>
        <TitleView
          evalSpec={evalSpec}
          evalPlan={selectedLogDetails?.plan}
          evalResults={selectedLogDetails?.results}
          runningMetrics={runningMetrics}
          evalStats={selectedLogDetails?.stats}
          status={selectedLogDetails?.status}
          tags={selectedLogDetails?.tags}
          collapsed={titleCollapsed || onSampleTerminals}
        />
        <div ref={divRef} className={clsx("workspace", styles.workspace)}>
          <div className={clsx("log-detail", styles.tabContainer)}>
            <TabSet
              id="log-details"
              tools={tabTools}
              type="pills"
              className={clsx(styles.tabSet, "text-size-smaller")}
              tabControlsClassName={clsx(styles.tabs, "text-size-smaller")}
              tabPanelsClassName={clsx(styles.tabPanels)}
            >
              {Object.keys(tabs).map((key) => {
                const tab = tabs[key];
                if (tab === undefined) return null;
                return (
                  <TabPanel
                    key={tab.id}
                    id={tab.id}
                    title={tab.label}
                    onSelected={onSelected}
                    href={toFullUrlMaybe(navigation.getTabUrl(tab.id))}
                    selected={selectedTab === tab.id}
                    scrollable={!!tab.scrollable}
                    scrollRef={tab.scrollable ? tab.scrollRef : undefined}
                    className={clsx(tab.className)}
                    style={{ height: tab.scrollable ? "100%" : undefined }}
                  >
                    {createElement(tab.component, tab.componentProps)}
                  </TabPanel>
                );
              })}
            </TabSet>
          </div>
        </div>
      </Fragment>
    );
  }
};
